import { useEffect, useState } from "react";
import { Box, Typography, Avatar, Divider, Skeleton } from "@mui/material";
import LogChangesGroup from "./LogChangesGroup";
import { startGetAdsUser } from "../../../../../actions/user";
import { useDispatchApp } from "../../../../../lib/redux";
import { DATE_SHORT } from "../../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../../lib/i18next";
import { logoPlatform } from "../../../../../utils/string";
import { META } from "../../../../../utils/constants";
import { useReportSnapshot } from "../../../../../features/reportPdf/ReportSnapshotContext";

const LogEntry = ({
  log,
  isLast,
  cachedNameEntities,
  onChangeCachedNameEntities,
  isExpanded = false,
  enableReverse = false,
  reverseContext = null,
}) => {
  const reportSnapshot = useReportSnapshot();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(!reportSnapshot);

  const name = user || log.userID;

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();

  // Group changes by objectType
  const groupedChanges = log.changes.reduce((acc, change) => {
    const objectType = change.objectType || "campaign";
    if (!acc[objectType]) {
      acc[objectType] = [];
    }
    acc[objectType].push(change);
    return acc;
  }, {});

  const getUser = async () => {
    if (cachedNameEntities[log.userID]) {
      setUser(cachedNameEntities[log.userID]);
      setIsLoading(false);
      return;
    }

    const response = await dispatch(startGetAdsUser(log.userID));
    if (response) {
      setUser(response.name);
      onChangeCachedNameEntities({
        [log.userID]: response.name,
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (reportSnapshot) {
      setUser(reportSnapshot.userNames?.[log.userID] || null);
      setIsLoading(false);
      return;
    }
    getUser();

    // eslint-disable-next-line
  }, []);

  // Check if any change in the entire log has isDiscarded=true
  const hasDiscardedChanges = log.changes.some(
    (change) => change.newValue?.isDiscarded === true
  );

  // If there are discarded changes, don't render the entire LogEntry
  if (hasDiscardedChanges) {
    return null;
  }

  return (
    <Box data-pdf-card sx={{ mb: isLast ? 0 : 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          {isLoading ? (
            <Skeleton variant="text" width={100} height={29} />
          ) : (
            <Typography variant="subtitle1" component="div">
              {t(DATE_SHORT, { date: new Date(log.creationTime) })}
            </Typography>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <Avatar
              key={log.platform}
              src={logoPlatform(log.platform)}
              alt={`${log.platform} Logo`}
              variant="square"
              sx={{
                width: log.platform === META ? 30 : 23,
                height: "auto",
                ".MuiAvatar-img": {
                  objectFit: "contain",
                },
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.25 }}
            >
              {name}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box>
        {Object.entries(groupedChanges).map(([objectType, changes], index) => {
          return (
            <LogChangesGroup
              campaignID={log.campaignID}
              key={`${objectType}-${index}`}
              objectType={objectType}
              changes={changes}
              cachedNameEntities={cachedNameEntities}
              onChangeCachedNameEntities={onChangeCachedNameEntities}
              isExpanded={isExpanded}
              platform={log.platform}
              enableReverse={enableReverse}
              reverseContext={reverseContext}
            />
          );
        })}
      </Box>

      {!isLast && <Divider sx={{ mt: 3 }} />}
    </Box>
  );
};

export default LogEntry;
